import { supabase } from "@/lib/supabase"
import { getFunctionsBaseUrl } from "@/utils/getFunctionsBaseUrl"

export type TimesheetStatus = "InProgress" | "Pending" | "Approved" | "Denied"

export interface Timesheet {
  id: string
  shiftId: string
  peopleId: string | null
  clockedInTime: string | null
  clockedOutTime: string | null
  break: number | null
  latitude: number
  longitude: number
  endlatitude: number | null
  endlongitude: number | null
  status: TimesheetStatus
  lastEditedBy: string | null
  autoClockOut: boolean
}

export type UnavailabilityType =
  "Holiday" | "Medical" | "Birthday" | "ReligiousObservance" | "PrivateMatter"

export interface Unavailability {
  id: string
  peopleId: string
  startTime: string
  endTime: string
  status: "Pending" | "Approved" | "Denied"
  type: UnavailabilityType
  notes: string | null
}

async function withAuthHeaders() {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return {
    Authorization: token ? `Bearer ${token}` : "",
    apikey: (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string) || "",
    "Content-Type": "application/json",
  } as Record<string, string>
}

export interface ClockInBody {
  shiftId: string
  latitude: number
  longitude: number
  clockedInTime?: string // optional per API, server uses now
}

export interface ClockOutBody {
  id: string // timesheet id
  clockedOutTime: string // ISO
  endlatitude?: number | null
  endlongitude?: number | null
}

export interface UpdateTimesheetBody {
  id: string
  clockedInTime?: string
  clockedOutTime?: string
  break?: number
  status?: TimesheetStatus
}

export interface CreateUnavailabilityBody {
  startTime: string
  endTime: string
  type: UnavailabilityType
  notes?: string
}

export interface ForceClockInBody {
  shiftId: string
  clockedInTime?: string
}

export interface ForceClockOutBody {
  timesheetId: string
  clockedOutTime?: string
}

export const timesheetsService = {
  async clockIn(body: ClockInBody): Promise<Timesheet> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/timesheets/clockin`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as { success: boolean; result?: Timesheet; error?: string }
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to clock in (status ${res.status})`)
    }
    return json.result as Timesheet
  },

  async clockOut(body: ClockOutBody): Promise<Timesheet> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/timesheets/clockout`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as { success: boolean; result?: Timesheet; error?: string }
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to clock out (status ${res.status})`)
    }
    return json.result as Timesheet
  },

  async updateTimesheet(body: UpdateTimesheetBody): Promise<Timesheet> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/timesheets`, {
      method: "PUT",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as { success: boolean; result?: Timesheet; error?: string }
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to update timesheet (status ${res.status})`)
    }
    return json.result as Timesheet
  },

  async forceClockIn(body: ForceClockInBody): Promise<Timesheet> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/timesheets/force-clockin`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as { success: boolean; result?: Timesheet; error?: string }
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to force clock in (status ${res.status})`)
    }
    return json.result as Timesheet
  },

  async forceClockOut(body: ForceClockOutBody): Promise<Timesheet> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/timesheets/force-clockout`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as { success: boolean; result?: Timesheet; error?: string }
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to force clock out (status ${res.status})`)
    }
    return json.result as Timesheet
  },

  async createUnavailability(body: CreateUnavailabilityBody): Promise<Unavailability> {
    const headers = await withAuthHeaders()
    const res = await fetch(`${getFunctionsBaseUrl()}/timesheets/unavailability`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as { success: boolean; result?: Unavailability; error?: string }
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to create unavailability (status ${res.status})`)
    }
    return json.result as Unavailability
  },
}
